// src/components/pages/CommitteePage.js - Updated to support both House and Senate with DeskCarousel and theme support
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Breadcrumb, Button, Alert } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, House, Building, Pen } from 'react-bootstrap-icons';
import { getHouseCommittee, getSenateCommittee } from '../../utils/feedsConfig';
import FeedSection from '../feed/FeedSection';
import MaterialsFeedSection from '../feed/MaterialsFeedSection';
import DeskCarousel from '../carousel/DeskCarousel';
import SearchNavBar from '../search/SearchNavBar';
import MobileNavigation from '../navigation/MobileNavigation';
import openCapacitorSite from '../../utils/browserUtils';
import './CommitteePage.css';

const CommitteePage = ({ chamber: chamberProp, carouselInterval = 5000 }) => {
  const { committeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [committee, setCommittee] = useState(null);

  // Determine chamber from prop or URL
  const chamber = chamberProp || (location.pathname.includes('/senate/') ? 'senate' : 'house');
  const isSenate = chamber === 'senate';

  useEffect(() => {
    const committeeData = isSenate 
      ? getSenateCommittee(committeeId)
      : getHouseCommittee(committeeId);
    setCommittee(committeeData);
  }, [committeeId, isSenate]);

  const handleBack = () => {
    navigate(isSenate ? '/us/senate' : '/us/house');
  };

  /**
   * Generate feed variations with different skip values for carousel display
   */
  const generateFeedVariations = (baseFeed, count = 10) => {
    const variations = [];
    for (let i = 0; i < count; i++) {
      let endpoint = baseFeed.apiEndpoint || baseFeed.apiParams;
      
      if (endpoint.includes('skip=')) {
        endpoint = endpoint.replace(/skip=\d+/, `skip=${i}`);
      } else {
        endpoint += `&skip=${i}`;
      }
      
      variations.push({
        ...baseFeed,
        id: `${baseFeed.id}-${i}`,
        apiEndpoint: endpoint,
        title: baseFeed.title || baseFeed.name
      });
    }
    return variations;
  };

  /**
   * Get the primary feed based on chamber type
   * House uses meetingFeed, Senate uses newsFeed
   */
  const getPrimaryFeed = useCallback(() => {
    if (!committee) return null;
    const feed = isSenate ? committee?.newsFeed : committee?.meetingFeed;
    return feed || null;
  }, [committee, isSenate]);

  /**
   * Get the primary feed title based on chamber type
   */
  const getPrimaryFeedTitle = () => {
    return isSenate ? 'Committee News' : 'Committee Meetings';
  };

  /**
   * Parse the materials feed endpoint to extract committee parameters
   * and generate the FullFeedPage URL
   */
  const generateMaterialsFeedUrl = useCallback(() => {
    const materialsFeed = committee?.materialsFeed;
    if (!materialsFeed?.apiEndpoint) {
      return '/feed/united_states_of_america/committee_materials';
    }

    const endpoint = materialsFeed.apiEndpoint;
    
    let cleanEndpoint = endpoint;
    if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
      cleanEndpoint = cleanEndpoint.substring(1);
    }
    
    try {
      const urlParams = new URLSearchParams(cleanEndpoint);
      const committeeName = urlParams.get('committee');
      const chamberParam = urlParams.get('chamber');
      
      const searchParams = new URLSearchParams();
      searchParams.set('useCommitteeAPI', 'true');
      
      if (committeeName) {
        searchParams.set('committee', committeeName);
      }
      if (chamberParam) {
        searchParams.set('chamber', chamberParam);
      }
      
      return `/feed/united_states_of_america/committee_materials?${searchParams.toString()}`;
    } catch (err) {
      console.error('Error generating materials feed URL:', err);
      return '/feed/united_states_of_america/committee_materials';
    }
  }, [committee]);

  /**
   * Parse the primary feed endpoint to extract standard feed parameters
   * and generate the FullFeedPage URL.
   *
   * FIX 1: Read snake_case (filter_column/filter_string) from the apiEndpoint
   * but output camelCase (filterColumn/filterString) in the URL — that is what
   * FullFeedPage reads from location.search.
   */
  const generatePrimaryFeedUrl = useCallback(() => {
    const primaryFeed = getPrimaryFeed();
    if (!primaryFeed?.apiEndpoint) {
      return isSenate 
        ? '/feed/united_states_of_america/senate_committee_news'
        : '/feed/united_states_of_america/committee_meetings';
    }

    const endpoint = primaryFeed.apiEndpoint;
    
    let cleanEndpoint = endpoint;
    if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
      cleanEndpoint = cleanEndpoint.substring(1);
    }
    
    try {
      const urlParams = new URLSearchParams(cleanEndpoint);
      const schema = urlParams.get('schema') || 'united_states_of_america';
      const table = urlParams.get('table') || (isSenate ? 'senate_committee_news' : 'committee_meetings');
      
      // Read snake_case from apiEndpoint, output camelCase for FullFeedPage
      const filterColumn = urlParams.get('filter_column');
      const filterString = urlParams.get('filter_string');
      
      let url = `/feed/${schema}/${table}`;
      if (filterColumn && filterString) {
        url += `?filterColumn=${encodeURIComponent(filterColumn)}&filterString=${encodeURIComponent(filterString)}`;
      }
      
      return url;
    } catch (err) {
      console.error('Error generating primary feed URL:', err);
      return isSenate 
        ? '/feed/united_states_of_america/senate_committee_news'
        : '/feed/united_states_of_america/committee_meetings';
    }
  }, [getPrimaryFeed, isSenate]);

  /**
   * Transform primary feed for DeskCarousel format
   */
  const transformPrimaryFeedForCarousel = useCallback(() => {
    const primaryFeed = getPrimaryFeed();
    if (!primaryFeed) return null;
    
    return {
      type: 'normal',
      id: primaryFeed.id || 'primary-feed',
      title: getPrimaryFeedTitle(),
      apiEndpoint: primaryFeed.apiEndpoint,
      feedName: primaryFeed.feedName,
      branch: 'Legislative',
      topic: 'N/A',
      buttonName: primaryFeed.buttonName,
      viewAllLink: generatePrimaryFeedUrl()
    };
  }, [getPrimaryFeed, generatePrimaryFeedUrl]);

  // Chamber-specific styling
  const chamberIcon = isSenate ? <Building className="me-1" /> : <House className="me-1" />;
  const chamberName = isSenate ? 'United States Senate' : 'House of Representatives';
  const chamberPath = isSenate ? '/us/senate' : '/us/house';

  if (!committee) {
    return (
      <>
        {/* Mobile Layout */}
        <div id="mobilePageContainer" style={{ display: 'block' }}>
          <div id='homebg'>
            <div style={{ marginTop: '100px', paddingTop: '20px' }}>
              <Alert variant="warning">
                Committee not found. <Button variant="link" onClick={handleBack}>Return to {isSenate ? 'Senate' : 'House'} page</Button>
              </Alert>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div id="desktopPageContainer" style={{ display: 'none' }}>
          <div id='homebg'>
            <Alert variant="warning">
              Committee not found. <Button variant="link" onClick={handleBack}>Return to {isSenate ? 'Senate' : 'House'} page</Button>
            </Alert>
          </div>
        </div>

        <style jsx>{`
          @media (max-width: 767px) {
            #desktopPageContainer { display: none !important; }
            #mobilePageContainer { display: block !important; }
          }
          @media (min-width: 768px) {
            #mobilePageContainer { display: none !important; }
            #desktopPageContainer { display: block !important; }
          }
        `}</style>
      </>
    );
  }

  const primaryFeed = getPrimaryFeed();
  const transformedPrimaryFeed = transformPrimaryFeedForCarousel();

  return (
    <>
      {/* Mobile Layout */}
      <div id="mobilePageContainer" className="committee-page-root" style={{ display: 'block' }}>
        <div id='homebg'>
          <div style={{ marginTop: '45px', paddingTop: '20px' }}>
            
            {/* Section Header */}
            <div className="mobile-section-header" style={{ padding: '15px', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div className="committee-profile-image-mobile" style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  overflow: 'hidden',
                  background: 'var(--bg-card)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Pen size={28} className="committee-icon" style={{ color: 'var(--text-primary)' }} />
                </div>
                
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px', fontWeight: '600', flex: 1, textAlign: 'center', padding: '0 10px' }}>
                  {isSenate ? 'Senate' : 'House'} {committee.name}
                </h3>
                
                <div style={{ width: '60px' }}></div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '12px', 
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {committee.description}
                </div>
                
                {committee.officialURL && (
                  <div style={{ marginTop: '8px' }}>
                    <a 
                      href={committee.officialURL}
                      onClick={(e) => {
                        e.preventDefault();
                        openCapacitorSite(committee.officialURL);
                      }}
                      style={{ color: 'var(--accent-hover)', fontSize: '12px' }}
                    >
                      Official Committee Site
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Feed Sections - Carousel Style */}
            
            {/* Primary Feed Carousel */}
            <div className="committee-feed-section">
              <div style={{ marginBottom: '15px', padding: '0 20px' }}>
                <h5 className="carousel-heading" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(generatePrimaryFeedUrl())}
                >
                  <div className="title-container">
                    <span className="heading-text">{getPrimaryFeedTitle()}</span>
                    <ChevronRight className="heading-chevron" />
                  </div>
                </h5>
              </div>
              
              <div className="carouselGroupContainer news-section">
                <div className="scroll-container">
                  <div className="gradient-left" />
                  <div className="gradient-right" />
                  <div className="scroll-area">
                    <div className="card-container">
                      {/* Feed Card */}
                      <div className="card-wrapper">
                        <div className="committee-card-content">
                          <FeedSection
                            title=""
                            apiEndpoint={primaryFeed?.apiEndpoint}
                            feedName={primaryFeed?.feedName}
                            branch="Legislative"
                            topic="N/A"
                            buttonName={primaryFeed?.buttonName}
                            maxItems={1}
                            enableNotifications={false}
                            showViewMore={false}
                          />
                        </div>
                      </div>
                      
                      {/* View More Card */}
                      <div className="card-wrapper view-all-card">
                        <div id="viewFullPageButton">
                          <Button 
                            id="viewFullLegislativePageButtonMobile" 
                            size="lg"
                            onClick={() => navigate(generatePrimaryFeedUrl())}
                          >
                            View all {getPrimaryFeedTitle()}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Committee Materials Carousel */}
            <div className="committee-feed-section">
              <div style={{ marginBottom: '15px', padding: '0 20px' }}>
                <h5 className="carousel-heading" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(generateMaterialsFeedUrl())}
                >
                  <div className="title-container">
                    <span className="heading-text">Committee Materials</span>
                    <ChevronRight className="heading-chevron" />
                  </div>
                </h5>
              </div>
              
              <div className="carouselGroupContainer news-section">
                <div className="scroll-container">
                  <div className="gradient-left" />
                  <div className="gradient-right" />
                  <div className="scroll-area">
                    <div className="card-container">
                      {/* Feed Card */}
                      <div className="card-wrapper">
                        <div className="committee-card-content">
                          <MaterialsFeedSection
                            title=""
                            apiEndpoint={committee?.materialsFeed?.apiEndpoint}
                            feedName={committee?.materialsFeed?.feedName}
                            branch="Legislative"
                            topic="N/A"
                            buttonName={committee?.materialsFeed?.buttonName}
                            maxItems={1}
                            showViewMore={false}
                          />
                        </div>
                      </div>
                      
                      {/* View More Card */}
                      <div className="card-wrapper view-all-card">
                        <div id="viewFullPageButton">
                          <Button 
                            id="viewFullLegislativePageButtonMobile" 
                            size="lg"
                            onClick={() => navigate(generateMaterialsFeedUrl())}
                          >
                            View all Committee Materials
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div id="desktopPageContainer" className="committee-page-root" style={{ display: 'none' }}>
        <div id='homebg'>
          {/* Breadcrumb Navigation */}
          <div style={{ padding: '30px 60px 20px' }}>
            <Breadcrumb>
              <Breadcrumb.Item 
                onClick={() => navigate('/us')}
                style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                United States
              </Breadcrumb.Item>
              <Breadcrumb.Item 
                onClick={() => navigate(chamberPath)}
                style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                {chamberIcon}
                {chamberName}
              </Breadcrumb.Item>
              <Breadcrumb.Item active style={{ color: 'var(--text-muted)' }}>
                {committee.shortName}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>

          {/* Profile Section */}
          <div style={{ position: 'relative', padding: '30px 0' }}>
            <div id='profileImage' style={{ 
              width: '130px', 
              height: '130px', 
              borderRadius: '100%', 
              overflow: 'hidden', 
              marginLeft: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-card)'
            }}>
              <Pen size={56} className="committee-icon" style={{ color: 'var(--text-primary)' }} />
            </div>

            <div style={{ 
              position: 'absolute', 
              top: '0', 
              left: '250px', 
              display: 'flex', 
              flexDirection: 'column',
              width: 'calc(100% - 250px)'
            }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '20px' }}> 
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>{isSenate ? 'Senate' : 'House'} {committee.name}</h3>
                </div>
                
                <div style={{ color: 'var(--text-secondary)', fontSize: '16px', width: '80%' }}>
                  <span>{committee.description}</span>
                  <br />
                  {committee.officialURL && (
                    <div style={{ color: 'var(--text-primary)', fontSize: '18px', marginTop: '10px' }}>
                      <span>
                        <a 
                          className="official-link"
                          style={{ color: 'var(--accent-hover)' }} 
                          href={committee.officialURL}
                          onClick={(e) => {
                            e.preventDefault();
                            openCapacitorSite(committee.officialURL);
                          }}
                        >
                          Visit Official Website
                        </a>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <br /><br />

          {/* Feed Sections - Using DeskCarousel */}
          <div id='branchPage' style={{ marginBottom: '1rem' }}>
            <Container className='bgcolor'>
              <Container id='carouselBufferOne'>
                <Row>
                  {/* Primary Feed - Meetings or News */}
                  <Col md={6}>
                    {transformedPrimaryFeed && (
                      <DeskCarousel 
                        title={getPrimaryFeedTitle()}
                        sections={generateFeedVariations(transformedPrimaryFeed, 10)}
                        viewAllLink={generatePrimaryFeedUrl()}
                        intervalDuration={carouselInterval}
                        showAll={true}
                        showFeedTitle={false}
                        useViewAllLinkForTitle={true}
                      />
                    )}
                  </Col>

                  {/* Committee Materials - uses DeskCarousel with useMaterialsFeed */}
                  <Col md={6}>
                    {committee?.materialsFeed && (
                      <DeskCarousel 
                        title="Committee Materials"
                        sections={generateFeedVariations({
                          type: 'normal',
                          id: 'materials-feed',
                          title: 'Committee Materials',
                          apiEndpoint: committee.materialsFeed.apiEndpoint,
                          feedName: committee.materialsFeed.feedName,
                          branch: 'Legislative',
                          topic: 'N/A',
                          buttonName: committee.materialsFeed.buttonName,
                          viewAllLink: generateMaterialsFeedUrl()
                        }, 10)}
                        viewAllLink={generateMaterialsFeedUrl()}
                        intervalDuration={carouselInterval}
                        showAll={true}
                        showFeedTitle={false}
                        useViewAllLinkForTitle={true}
                        useMaterialsFeed={true}
                      />
                    )}
                  </Col>
                </Row>
              </Container>
            </Container>
          </div>
        </div>
      </div>

      {/* Responsive CSS for mobile/desktop switching */}
      <style>{`
        @media (max-width: 767px) {
          #desktopPageContainer { display: none !important; }
          #mobilePageContainer { display: block !important; }
        }
        @media (min-width: 768px) {
          #mobilePageContainer { display: none !important; }
          #desktopPageContainer { display: block !important; }
        }
      `}</style>
    </>
  );
};

export default CommitteePage;